/**
 * Contracts for `pending-test-catalogue-spec` — the Business
 * persona's "what does our app provably do today?" view.
 *
 * No new persistence — the catalogue is a derived projection over
 * `flows`, `flow_tags`, `flow_owners`, `test_runs`, and
 * `test_authoring.linked_ac_ids`. Caches at the API layer behind
 * the project's `last_updated_at` watermark.
 */

import { z } from 'zod';

export const CatalogueRunChipSchema = z.object({
  runId: z.string().uuid(),
  status: z.enum(['passed', 'failed', 'flaky', 'running', 'pending', 'cancelled']),
  durationMs: z.number().int().nonnegative().nullable(),
  ranAt: z.string().datetime(),
  /** Linked HTML report row (`test_reports.id`) when one exists for this run. */
  reportId: z.string().uuid().nullable().optional(),
});
export type CatalogueRunChip = z.infer<typeof CatalogueRunChipSchema>;

export const CatalogueAcLinkSchema = z.object({
  /** Human-facing AC id (e.g. `AC-103`, free-text in test_authoring). */
  acId: z.string().min(1),
  /** Heuristic status derived from the linked flow's last-run state. */
  status: z.enum(['passing', 'failing', 'quarantined', 'unknown']),
});
export type CatalogueAcLink = z.infer<typeof CatalogueAcLinkSchema>;

export const CatalogueFlowCardSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  /** Markdown description (rendered client-side). */
  description: z.string().nullable(),
  /** Tag display names for this flow (already de-duplicated). */
  tags: z.array(z.string()),
  /** Optional flow owner (or `null` → "Unassigned"). */
  ownerName: z.string().nullable(),
  /** Step / assertion counts pulled cheaply from `flows.steps_count`
   *  (when present) or computed at projection time. */
  stepCount: z.number().int().nonnegative(),
  assertionCount: z.number().int().nonnegative(),
  /** Status chip — overall last-run state. `unknown` when never run. */
  lastRunStatus: z.enum(['passed', 'failed', 'flaky', 'unknown']),
  /** Last 3 runs (newest first). Empty if never run. */
  lastRuns: z.array(CatalogueRunChipSchema).max(3),
  /** Linked acceptance criteria (from test_authoring.linked_ac_ids). */
  linkedAcs: z.array(CatalogueAcLinkSchema),
});
export type CatalogueFlowCard = z.infer<typeof CatalogueFlowCardSchema>;

export const CatalogueSectionSchema = z.object({
  /** Tag-display string OR the synthetic `__uncategorised` bucket. */
  tag: z.string(),
  /** Cards under this section. */
  flows: z.array(CatalogueFlowCardSchema),
  /** Aggregate stats for the section footer. */
  stats: z.object({
    totalFlows: z.number().int().nonnegative(),
    totalAcs: z.number().int().nonnegative(),
    percentGreenLast7Days: z.number().min(0).max(100),
  }),
});
export type CatalogueSection = z.infer<typeof CatalogueSectionSchema>;

export const CatalogueResponseSchema = z.object({
  projectId: z.string().uuid(),
  projectName: z.string(),
  generatedAt: z.string().datetime(),
  /** Display sections, in order. Untagged flows live under
   *  `__uncategorised` last. */
  sections: z.array(CatalogueSectionSchema),
  /** Optional banner for uncovered ACs (ACs that exist in
   *  test_authoring but no flow links to them). v1 returns the
   *  list; UI decides whether to render the banner. */
  uncoveredAcs: z.array(
    z.object({
      acId: z.string(),
      sourceTestAuthoringId: z.string().uuid(),
      /** AC sentence when known — used to prefill the insert composer. */
      text: z.string().min(1).max(500).optional(),
    }),
  ),
  /** Top-level stats summary for the header. */
  stats: z.object({
    totalFlows: z.number().int().nonnegative(),
    totalAcs: z.number().int().nonnegative(),
    coveredAcs: z.number().int().nonnegative(),
    percentGreenLast7Days: z.number().min(0).max(100),
  }),
});
export type CatalogueResponse = z.infer<typeof CatalogueResponseSchema>;

export const CreateCatalogueShareLinkRequestSchema = z.object({
  expiresAt: z.string().datetime().optional(),
  rateLimitPerMin: z.number().int().min(1).max(60_000).optional(),
});
export type CreateCatalogueShareLinkRequest = z.infer<typeof CreateCatalogueShareLinkRequestSchema>;
